import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setFavicon } from '@/store/slices/project';
import { selectFavicon } from '@/store/slices/settings/selectors';

import { ICONS } from '@ui/icons/types';
import ProjectColorIcon from '@ui/icons/icon/project-color-icon';
import Text from '@ui/text/text/text';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import UploadLarge from '@ui/uploads/upload-large';
import OptionalComponent from '@ui/optional-component/optional-component';

import { EColorVariablesV2 } from '@/types/theme-packs.types';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import Container from '@ui/container/container';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

const FaviconUploader = () => {
  const dispatch = useAppDispatch();
  const favicon = useAppSelector(selectFavicon);
  const { projectUuid } = useParams<{ projectUuid: string; language: string }>();

  const [uploading, setUpLoading] = useState(false);

  const handleFile = useCallback(
    async (file: File | FileList) => {
      if (!(file instanceof File)) {
        return;
      }

      setUpLoading(true);
      try {
        const endpoint = await GetUploadEndpoint({ filename: getUploadFilename(file.name) });

        if (isObject(endpoint) && endpoint.url) {
          const uploadedFavicon = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          SetItemContentType({ filename: uploadedFavicon, contentType: file.type }).catch(console.error);

          if (projectUuid && uploadedFavicon) {
            dispatch(setFavicon({ project_uuid: projectUuid, favicon: uploadedFavicon }));
          }
        }
      } catch (error) {
        console.error('Error during file upload:', error);
        handleImageUploadErrors(error);
      } finally {
        setUpLoading(false);
      }
    },
    [dispatch, projectUuid]
  );

  return (
    <Container className="flex jc-center">
      <TooltipPortal
        tooltipContent={
          <Text className="P3" color="var(--gray-true-dark-200)">
            32 x 32px png recommended
          </Text>
        }
        tooltipWrapperClass="border-radius-8 padding-2"
        childrenWrapperClass="width-32 height-32"
        position={TooltipPortalPosition.BOTTOM}
      >
        <UploadLarge
          displayImageUrl={favicon}
          maxSizeInBytes={20 * 1024}
          onFile={handleFile}
          uploading={uploading}
          allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: 'transparent',
            border: '1px dashed var(--gray-600)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
          placeholder="Favicon"
          CustomTitle={
            <OptionalComponent display={!favicon && !uploading}>
              <div className="flex-column ai-center gap-10">
                <ProjectColorIcon name={ICONS.UPLOAD} size={12} colorVariable={EColorVariablesV2.primary} />
                <Text color={getThemeColorCSSVariable()} className="S3">
                  Fav icon
                </Text>
              </div>
            </OptionalComponent>
          }
        />
      </TooltipPortal>
    </Container>
  );
};

export default FaviconUploader;
